import { buildMatchMapLoad, createTurretsFromPlan } from './matchMapLoad.js';
import { MatchRuntime } from './matchRuntime.js';
import { GameOrchestrator } from './gameOrchestrator.js';
import { debugDestroyNextVulnerableStructure } from './debugStructureDestroy.js';
import { Entity } from '../domain/entity.js';
import { canRecallInAram, canShopInAram } from '../domain/map/aramRules.js';
import { isSummonerSpellAllowed } from '../domain/map/summonerSpellMapRules.js';
import { ARAM_BRIDGE_WIDTH } from '../domain/map/aramScale.js';
import { resolvePlayerMatchResult } from '../domain/matchOutcome.js';

const ARAM_ID = 'aram_proving_grounds';

function step(id, ok, detail = null) {
  return { id, ok: ok === true, detail };
}

/**
 * runAramPostRuntimeRegression — Deterministic ARAM checklist (P4-01).
 * Path: load → seed → walls → tick (aura) → rules → nexus victory.
 */
export function runAramPostRuntimeRegression() {
  const steps = [];

  const plan = buildMatchMapLoad(ARAM_ID);
  steps.push(step('load', plan.mapId === ARAM_ID
    && plan.objectives.relics.length === 4
    && plan.jungleCamps.length === 0
    && plan.structureBlueprints.length >= 2));

  const player = new Entity({
    id: 'p4-player',
    type: 'champion',
    team: 'blue',
    x: 0,
    z: 0,
    hp: 500,
    maxHp: 1000,
    mp: 100,
    maxMp: 1000,
    mpRegen: 0,
    ms: 0
  });
  player.lastCombatTimer = 6;

  const runtime = new MatchRuntime({ mapId: 'test_map' });
  runtime.applyMapLoad(plan);
  const structures = createTurretsFromPlan(plan);
  runtime.seedSandbox({ player, champions: [], structures });
  steps.push(step('seed', runtime.mapId === ARAM_ID
    && runtime.entities.getStructures().length === 12
    && runtime.gameTime === 0));

  const xs = (plan.wallBoxes || []).map((b) => (b.minX + b.maxX) / 2);
  const half = ARAM_BRIDGE_WIDTH / 2;
  steps.push(step('walls', (plan.wallBoxes || []).length >= 6
    && xs.some((x) => x < -half)
    && xs.some((x) => x > half)));

  const orch = new GameOrchestrator();
  orch.tick(10, {
    championEntity: player,
    secondaryChampions: [],
    allTurretEntities: structures,
    activeMinions: [],
    activeProjectiles: [],
    mapId: ARAM_ID,
    entityRegistry: runtime.entities
  });
  steps.push(step('tick', player.aramAuraActive === true
    && player.outgoingHealingMultiplier === 0.8
    && player.mp === 115
    && player.hp === 507.5));

  const midLane = { ...player, isDead: false, x: 0, z: 0 };
  const onSpawn = {
    ...player,
    isDead: false,
    x: plan.spawnerSpecs[0].spawnX,
    z: plan.spawnerSpecs[0].spawnZ
  };
  steps.push(step('rules', canRecallInAram(player) === false
    && canShopInAram(midLane) === false
    && canShopInAram(onSpawn) === true
    && isSummonerSpellAllowed('teleport', ARAM_ID) === false
    && isSummonerSpellAllowed('flash', ARAM_ID) === true));

  const killed = [];
  for (let i = 0; i < 8; i++) {
    const r = debugDestroyNextVulnerableStructure(structures, 'red');
    if (!r.ok) break;
    killed.push(r.id);
  }
  const outcome = runtime.syncOutcome();
  const banner = resolvePlayerMatchResult(outcome, 'blue');
  steps.push(step('victory', killed.includes('red-nexus')
    && outcome.ended === true
    && outcome.winnerTeam === 'blue'
    && banner.kind === 'victory'
    && banner.title === 'VICTORIA', { killed }));

  return {
    ok: steps.every((s) => s.ok),
    mapId: ARAM_ID,
    steps
  };
}
